import { AlertTriangle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { BottomSheet } from '@/ui/bottom-sheet'
import { Button } from '@/ui/button'
import { ConfirmDialog } from '../../shared/confirm-dialog'
import { matchesStocktakeFilter, parseMoney, type StocktakeFilter } from '../filters'
import { skusForLocation } from '../location-skus'
import { LocationChip } from './location-chip'
import { StaffAvatar } from './staff-avatar'

function ExceptionChip({ label }: Readonly<{ label: string }>) {
  return (
    <span className="rounded-full border border-orange-500 bg-orange-40 px-3 py-1 text-xs font-medium text-foreground max-w-fit">
      {label}
    </span>
  )
}

/**
 * The per-location zoom-in that used to be `StocktakeLocationDetailScreen`.
 *
 * It is a sheet now because the Progress tab's filter and summary already live
 * on the parent, so re-rendering a full screen just to show one location's SKU
 * list hid the context the learner just set. The sheet still honours the
 * parent's `filter` so the SKU list agrees with the row the learner tapped.
 */
function StocktakeLocationSheet({
  open,
  locationCode,
  filter,
  onClose,
}: Readonly<{
  open: boolean
  locationCode: string | null
  filter: StocktakeFilter
  onClose: () => void
}>) {
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const loc = locationCode ?? '—'
  const allSkus = skusForLocation(loc)

  const rows = useMemo(
    () =>
      allSkus.filter((row) =>
        matchesStocktakeFilter(filter, { qty: row.qty, value: parseMoney(row.value) }),
      ),
    [allSkus, filter],
  )

  const exceptionCount = rows.filter((s) => s.exception !== undefined).length
  const totalQty = rows.reduce((sum, s) => sum + s.qty, 0)
  const totalValue = rows.reduce((sum, s) => sum + parseMoney(s.value), 0)

  return (
    <>
      <BottomSheet
        dataTour="stocktake-location-sheet"
        open={open}
        onClose={onClose}
        title={`Loc ${loc} · ${rows.length} SKUs`}
        scrollable={false}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          {exceptionCount > 0 && (
            <div
              data-tour="stocktake-location-exception-banner"
              className="mb-3 flex items-center gap-2 rounded-xl bg-orange-40 px-3 py-3"
            >
              <AlertTriangle className="size-5 shrink-0 text-orange-500" />
              <p className="text-sm font-medium text-foreground">
                {exceptionCount} {exceptionCount === 1 ? 'SKU has' : 'SKUs have'} exceptions
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pb-2">
            <div className="flex items-center gap-7">
              <span className="text-sm text-foreground">Loc</span>
              <span className="text-sm text-foreground">SKU/Description/Staff ID</span>
            </div>
            <span className="text-sm text-foreground">QTY/Value/UOM</span>
          </div>
          <div className="h-px w-full bg-border" />

          <div className="min-h-0 flex-1 overflow-y-auto">
            {rows.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No SKUs match your filter.
              </p>
            )}
            {rows.map((row, i) => (
              <div key={row.sku}>
                <div
                  data-tour={
                    row.exception !== undefined &&
                    rows.findIndex((r) => r.exception !== undefined) === i
                      ? 'stocktake-location-exception-sku'
                      : undefined
                  }
                  className="flex items-start py-2"
                >
                  <div className="flex flex-1 items-start gap-7">
                    <div className="pt-1">
                      <LocationChip code={loc} />
                    </div>
                    <div className="flex flex-1 flex-col gap-1">
                      <p className="text-base font-bold text-foreground">{row.sku}</p>
                      <p className="text-sm text-foreground">{row.name}</p>
                      {row.exception && <ExceptionChip label={row.exception} />}
                      <div className="flex items-center gap-2">
                        <StaffAvatar name={row.staff} index={i} />
                        <p className="text-xs font-medium text-foreground">{row.staff}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ml-2 flex flex-col items-end gap-1 text-right">
                    <p className="text-base font-bold text-foreground">{row.qty}</p>
                    <p className="text-sm text-foreground">{row.value}</p>
                    <p className="text-sm text-foreground">{row.uom}</p>
                  </div>
                </div>
                {i < rows.length - 1 && <div className="h-px w-full bg-border" />}
              </div>
            ))}

            <div className="h-px w-full bg-border" />
            <div className="flex items-center justify-between py-2 text-xs font-medium text-foreground">
              <span>Total: {rows.length}</span>
              <span>QTY: {totalQty.toLocaleString()}</span>
              <span>
                Value: ${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          <div className="shrink-0 bg-card pt-4">
            <div className="flex flex-col gap-4">
              <Button className="h-12 w-full rounded-full" onClick={onClose}>
                Okay
              </Button>
              <Button
                variant="outline"
                className="h-12 w-full rounded-full border-foreground"
                onClick={() => setRemoveDialogOpen(true)}
              >
                Remove location count
              </Button>
            </div>
          </div>
        </div>
      </BottomSheet>

      <ConfirmDialog
        open={removeDialogOpen}
        iconVariant="warning"
        titleVariant="default"
        title={`Remove location count data for Loc ${loc}?`}
        description={
          <p className="text-base text-black">
            {totalQty.toLocaleString()} items counted and will be deleted. Loc {loc} goes back to
            Not started and will need to be counted again. This can&apos;t be undone.
          </p>
        }
        primaryLabel="Remove location count"
        onPrimary={() => {
          setRemoveDialogOpen(false)
          onClose()
        }}
        secondaryLabel="Cancel"
        onSecondary={() => setRemoveDialogOpen(false)}
      />
    </>
  )
}

export { StocktakeLocationSheet }
